<script setup lang="ts">
import { defineProps, defineEmits, computed, withDefaults } from "vue";
import DiamondsSearchFormIcons from "@/components/diamonds/form/diamondsSearchFormIcons.vue";
import DiamondsSearchFormNumberRange from "@/components/diamonds/form/diamondsSearchFormNumberRange.vue";
import DiamondsSearchFormRange from "@/components/diamonds/form/diamondsSearchFormRange.vue";
import DiamondsSearchFormSwitch from "@/components/diamonds/form/diamondsSearchFormSwitch.vue";
import DiamondsSearchFormList from "@/components/diamonds/form/diamondsSearchFormList.vue";
import DiamondsSearchFormPresets from "@/components/diamonds/form/diamondsSearchFormPresets.vue";
import DiamondsSearchFormTextList from "@/components/diamonds/form/diamondsSearchFormTextList.vue";
import InfoModal from "@/components/info/infoModal.vue";

const props = withDefaults(
  defineProps<{
    filters: any;
    activeFilters: any;
    singleFilterKey?: string;
    isFilterDefined: (key: string) => boolean;
    isFilterOpen: (key: string) => boolean;
    toggleFilter: (key: string) => void;
    getFilterLabel: (key: string) => string;
    getActiveValueLabel: (key: string, value: any) => string;
    presets?: any[];
  }>(),
  {
    filters: () => ({}),
    activeFilters: () => ({}),
    singleFilterKey: "",
    presets: () => [],
  }
);

const emit = defineEmits<{
  (e: "updateFilter", key: string, value: any): void;
  (e: "updateFilters", value: any): void;
}>();

const sortedFilterKeys = computed(() => {
  return Object.keys(props.filters).sort((a, b) => {
    return (props.filters[a].order || 0) - (props.filters[b].order || 0);
  });
});

const alwaysVisibleKeys = computed(() => {
  if (props.singleFilterKey) {
    return sortedFilterKeys.value.filter(key => key === props.singleFilterKey && key === 'type');
  }
  return sortedFilterKeys.value.filter(key => key === 'type');
});

const collapsibleKeys = computed(() => {
  if (props.singleFilterKey) {
    return sortedFilterKeys.value.filter(key => {
      const isTarget = key === props.singleFilterKey || (props.singleFilterKey === 'colored' && key === 'color');
      if (!isTarget) return false;
      if (key === 'type' || key === 'sortOrder' || key === 'colored') {
        return false;
      }
      if (key === 'fancy_intensity' && !props.activeFilters.colored) {
        return false;
      }
      return true;
    });
  }
  return sortedFilterKeys.value.filter(key => {
    if (key === 'type' || key === 'sortOrder' || key === 'colored') {
      return false;
    }
    if (key === 'fancy_intensity' && !props.activeFilters.colored) {
      return false;
    }
    return true;
  });
});

const showBottomFilters = computed(() => {
  if (props.singleFilterKey) {
    return props.singleFilterKey === 'fastDelivery' || props.singleFilterKey === 'sortOrder';
  }
  return true;
});

const updateFilter = (key: string, val: any) => {
  emit('updateFilter', key, val);
}

</script>

<template>
  <div class="diamonds-search-form-pro">
    <!-- Always visible filters (type and colored) -->
    <template v-for="key in alwaysVisibleKeys" :key="'always-' + key">
      <div v-if="filters[key]" class="filter-row border-b border-gray-200 py-4">
        <div class="mt-2 overflow-x-auto no-scrollbar scroll-smooth flex">
          <template v-if="filters[key].type === 'icon' && filters[key].view === 'list'">
            <DiamondsSearchFormIcons
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="filters[key].type === 'number' && filters[key].view === 'range'">
            <DiamondsSearchFormNumberRange
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="(filters[key].type === 'icon' || filters[key].type === 'text') && filters[key].view === 'range'">
            <DiamondsSearchFormRange
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="(filters[key].type === 'text' || filters[key].type === 'bool') && filters[key].view === 'switch'">
            <DiamondsSearchFormSwitch
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
            <info-modal :id="filters[key].modalId" :value="activeFilters[key]" />
          </template>
          <template v-else-if="filters[key].type === 'text' && filters[key].view === 'list'">
            <DiamondsSearchFormList
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
        </div>
      </div>
    </template>

    <DiamondsSearchFormPresets
      v-if="presets && presets.length > 0 && (typeof activeFilters.colored == 'undefined' || activeFilters.colored !== true)"
      :presets="presets"
      :active-filters="activeFilters"
      class="mt-2 border-b border-gray-200"
      @update-filters="(val) => emit('updateFilters', val)"
    />

    <template v-for="key in collapsibleKeys" :key="key">
      <div v-if="filters[key]" class="filter-row border-b border-gray-200 py-4 last:border-b-0">
        <div class="flex justify-between items-center cursor-pointer group" @click="toggleFilter(key)">
          <div class="flex gap-2 items-center">
            <h3 class="font-bold text-sm text-gray-900 tracking-tight">{{ getFilterLabel(key) }}:</h3>
            <span class="text-sm text-gray-500 font-light">{{ getActiveValueLabel(key, activeFilters[key]) }}</span>
            <info-modal :id="filters[key].modalId" :value="activeFilters[key]" :stone-type="activeFilters['type'] || 'earth'" />
          </div>
          <div v-if="!isFilterDefined(key)" class="text-gray-400 group-hover:text-gray-500 transition-colors">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4 transition-transform duration-300"
              :class="{ 'rotate-180': isFilterOpen(key) }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <div v-if="isFilterOpen(key)" class="mt-3 overflow-x-auto no-scrollbar scroll-smooth">
          <template v-if="key === 'color' && filters.colored">
            <div class="mb-6 px-2">
              <DiamondsSearchFormSwitch
                :filter="filters.colored"
                :active-value="activeFilters.colored"
                @update="(val) => updateFilter('colored', val)"
              />
            </div>
          </template>

          <template v-if="key === 'color' && activeFilters.colored">
            <DiamondsSearchFormList
              :filter="{...filters[key], values: filters[key].fancy_colors}"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="filters[key].type === 'icon' && filters[key].view === 'list'">
            <DiamondsSearchFormIcons
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="filters[key].type === 'number' && filters[key].view === 'range'">
            <DiamondsSearchFormNumberRange
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="(filters[key].type === 'icon' || filters[key].type === 'text') && filters[key].view === 'range'">
            <DiamondsSearchFormRange
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="(filters[key].type === 'text' || filters[key].type === 'bool') && filters[key].view === 'switch'">
            <DiamondsSearchFormSwitch
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="filters[key].type === 'text' && filters[key].view === 'list'">
            <DiamondsSearchFormTextList
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <div v-else class="flex gap-4 pb-2 px-2">
            <!-- Standard values -->
            <template v-if="filters[key].values">
              <div
                v-for="(label, val) in filters[key].values"
                :key="val"
                class="flex flex-col items-center cursor-pointer min-w-fit"
                @click="updateFilter(key, val)"
              >
                <div
                  class="w-20 h-20 rounded-full border flex items-center justify-center mb-2 transition-all duration-300"
                  :class="activeFilters[key] === val ? 'border-brand-main border-[1.5px] bg-gray-50 shadow-sm' : 'border-gray-100 hover:border-gray-300'"
                >
                  <!-- Standard values -->
                  <template v-if="key === 'color' || key === 'fancy_intensity'">
                     <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600">
                       {{ val }}
                     </div>
                  </template>

                  <!-- Other types -->
                  <div v-else class="text-xs font-medium text-gray-700">
                    {{ val }}
                  </div>
                </div>
                <span class="text-[11px] text-gray-500 tracking-wide whitespace-nowrap">{{ label }}</span>
              </div>
            </template>
            
            <!-- Fancy Colors -->
            <template v-if="filters[key].fancy_colors">
               <div v-for="(label, val) in filters[key].fancy_colors" :key="'fancy-'+val" class="flex flex-col items-center cursor-pointer min-w-fit" @click="updateFilter(key, val)">
                  <div class="w-20 h-20 rounded-full border flex items-center justify-center mb-2 transition-all" :class="activeFilters[key] === val ? 'border-brand-main border-[1.5px] bg-gray-50' : 'border-gray-100'">
                     <div class="w-14 h-14 rounded-full shadow-inner" :style="{ backgroundColor: val.toLowerCase() }"></div>
                  </div>
                  <span class="text-[11px] text-gray-500 tracking-wide whitespace-nowrap">{{ label }}</span>
               </div>
            </template>

            <!-- Range/Size/Price special handling if needed -->
            <template v-if="key === 'size' && !filters[key].values">
               <div v-for="s in [0.2, 0.3, 0.4, 0.5, 0.7, 1.0, 1.5, 2.0]" :key="s" class="flex flex-col items-center cursor-pointer min-w-fit" @click="updateFilter(key, {min: s, max: s})">
                  <div class="w-20 h-20 rounded-full border flex items-center justify-center mb-2 transition-all" :class="activeFilters[key] && activeFilters[key].min === s ? 'border-brand-main border-[1.5px]' : 'border-gray-100'">
                     <div class="flex flex-col items-center">
                        <div class="w-1 h-6 bg-gray-300 mb-1"></div> <!-- Dummy ring side view icon -->
                        <span class="text-[10px]">{{ s.toLocaleString('de-DE', { minimumFractionDigits: 2 }) }} ct.</span>
                     </div>
                  </div>
                  <span class="text-[11px] text-gray-500">{{ s.toLocaleString('de-DE', { minimumFractionDigits: 2 }) }} ct.</span>
               </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <div v-if="false" class="mt-8 pt-8 border-t border-[#a99d8b] flex items-center justify-between gap-4">
      <div v-if="!singleFilterKey || singleFilterKey === 'fastDelivery'" class="flex items-center gap-3">
        <button 
          @click="updateFilter('fastDelivery', !activeFilters.fastDelivery)"
          class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
          :style="{ backgroundColor: activeFilters.fastDelivery ? '#a99d8b' : '#e5e7eb' }"
        >
          <span
            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            :class="activeFilters.fastDelivery ? 'translate-x-5' : 'translate-x-0'"
          />
        </button>
        <span class="text-[13px] uppercase tracking-widest text-[#4A5568]">Fast-Delivery</span>
      </div>

      <div v-if="!singleFilterKey || singleFilterKey === 'sortOrder'" class="flex items-center gap-3">
        <button 
          @click="updateFilter('sortOrder', activeFilters.sortOrder === 'asc' ? 'desc' : 'asc')"
          class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
          :style="{ backgroundColor: '#a99d8b' }"
        >
          <span
            class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
            :class="activeFilters.sortOrder === 'desc' ? 'translate-x-5' : 'translate-x-0'"
          />
        </button>
        <span class="text-[13px] uppercase tracking-widest text-[#4A5568]">Sort: {{ activeFilters.sortOrder === 'asc' ? 'Auf' : 'Ab' }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>